import { rem } from 'polished'
import React, { useRef } from 'react'
import styled from 'styled-components'
import { Navigation, Pagination, EffectFade, Autoplay, A11y } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import {
  BULLET_WRAPPER_SIZE_PX,
  CarouselNavigation,
} from 'components/CarouselNavigation'
import { Box, Container, Flex, Grid } from 'components/Layout'
import { Text } from 'components/Typography'
import { COLOR_NAMES, FONT_SIZE, FONT_STACK, SPACE, Z_INDEX } from 'Theme'

const BULLET_THRESHOLD = 10
const BULLET_ACTIVE_CLASS = 'gemma-questions-pagination-bullet-active'
const AUTOPLAY_DELAY = 10000

const QUESTIONS = [
  'What customization options are available for this piece?',
  "I'm working on a deadline. How quickly can this ship?",
  'How much will this cost to ship?',
]

const StyledSwiper = styled(Swiper)`
  user-select: none;
  height: 100%;
  .swiper-slide {
    height: auto;
  }
  .swiper-pagination-bullet {
    transform: scale(1) !important;
    transition: all 600ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  .${BULLET_ACTIVE_CLASS} {
    transform: scale(1.5) !important;
    background-color: ${COLOR_NAMES.PINEY_LAKE};
    opacity: 1;
  }
  .swiper-pagination-bullets {
    min-width: ${rem(BULLET_WRAPPER_SIZE_PX * BULLET_THRESHOLD)};
    column-gap: 20px;
    padding: 16px;
    position: relative;
  }
`

const AskButton = styled.button`
  background-color: ${COLOR_NAMES.PINEY_LAKE};
  color: white;
  border: none;
  border-radius: ${rem(4)};
  padding: ${rem(10)} ${rem(24)};
  font-family: ${FONT_STACK.SANS};
  font-size: ${rem(14)};
  cursor: pointer;
  margin-top: ${rem(20)};
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.85;
  }
`

const QuestionItem = ({ question }: { question: string }) => (
  <Flex
    justifyContent="center"
    flexDirection="column"
    alignItems="center"
    maxWidth={rem(300)}
    mx="auto"
  >
    <Text
      fontFamily={FONT_STACK.SERIF}
      fontSize={{ MOBILE: rem(18), TABLET: rem(20) }}
      textAlign="center"
      lineHeight="140%"
    >
      {question}
    </Text>
  </Flex>
)

export const GemmaQuestionsWidget = () => {
  const nextButtonRef = useRef(null)
  const prevButtonRef = useRef(null)
  const paginationRef = useRef(null)
  const swiper = useRef(null)

  return (
    <Box
      pb={{ MOBILE: SPACE.PX_48, TABLET: rem(66) }}
      pt={{ MOBILE: SPACE.PX_64, TABLET: rem(66) }}
      width="100%"
      overflow="hidden"
    >
      <Container>
        <Box
          mb={{ MOBILE: rem(48), TABLET: rem(56) }}
          style={{ textAlign: 'center' }}
        >
          <Text
            fontFamily={FONT_STACK.SERIF}
            fontSize={{ MOBILE: FONT_SIZE.PX_32, TABLET: rem(40) }}
            lineHeight={{ MOBILE: rem(44), TABLET: rem(52) }}
          >
            Have a question? Ask AI Chat
          </Text>
        </Box>
        <Grid
          gridTemplateColumns="repeat(3, 1fr)"
          gap={rem(32)}
          display={{ MOBILE: 'none', TABLET: 'grid' }}
        >
          {QUESTIONS.map((question) => (
            <QuestionItem key={question} question={question} />
          ))}
        </Grid>
        <Flex justifyContent="center" mt={{ MOBILE: rem(32), TABLET: rem(40) }}>
          <AskButton onClick={() => window.GemmaChat?.('show')}>
            Ask a question
          </AskButton>
        </Flex>
      </Container>
      <Box display={{ MOBILE: 'block', TABLET: 'none' }} mt={SPACE.PX_20}>
        <StyledSwiper
          ref={swiper}
          modules={[Autoplay, Navigation, Pagination, EffectFade, A11y]}
          navigation={{
            prevEl: prevButtonRef.current,
            nextEl: nextButtonRef.current,
          }}
          speed={750}
          pagination={{
            el: paginationRef.current,
            clickable: true,
            bulletActiveClass: BULLET_ACTIVE_CLASS,
          }}
          slidesPerView={1}
          direction="horizontal"
          loop
          autoplay={{ delay: AUTOPLAY_DELAY, disableOnInteraction: false }}
          allowTouchMove
        >
          {QUESTIONS.map((question) => (
            <SwiperSlide key={question}>
              <QuestionItem question={question} />
            </SwiperSlide>
          ))}
          <Flex
            position="relative"
            width="100%"
            px={SPACE.PX_16}
            justifyContent="center"
            zIndex={Z_INDEX.CAROUSEL_NAVIGATION_WRAPPER}
            mt={rem(20)}
          >
            <CarouselNavigation
              prevButtonRef={prevButtonRef}
              nextButtonRef={nextButtonRef}
              paginationRef={paginationRef}
              hasArrows={false}
            />
          </Flex>
        </StyledSwiper>
      </Box>
    </Box>
  )
}
